import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, ArrowUp, RotateCcw } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Eyebrow, Card, Index, Lead, SideImage, FlowArrow } from '../components/common/Primitives';
import {
  systemMeta, layerGlance, invariants, dataFlow, flowNotes,
  controlPanelModules, closingRules,
} from '../data/system';
import banner from '../assets/banners/system.jpg';
import edgeMonitoring from '../assets/tech/edge-monitoring.jpg';

const step = (n) => dataFlow.find((s) => s.n === n);

const cardTone = (accent) =>
  accent ? 'bg-amber/[0.08] ring-amber/30' : 'bg-white/[0.04] ring-white/10';

const StepHead = ({ s }) => (
  <div className="flex items-center gap-3">
    <span
      className={`w-8 h-8 rounded-md grid place-items-center font-mono text-[12.5px] font-semibold shrink-0 ring-1 ${
        s.accent ? 'bg-amber/15 ring-amber/40 text-amber' : 'bg-lime/10 ring-lime/30 text-lime'
      }`}
    >
      {String(s.n).padStart(2, '0')}
    </span>
    <p className={`label ${s.accent ? 'text-amber' : 'text-lime'}`}>{s.stage}</p>
  </div>
);

const FlowCard = ({ s, className = '' }) => (
  <div className={`rounded-lg p-5 ring-1 ${cardTone(s.accent)} ${className}`}>
    <StepHead s={s} />
    <p className="mt-3.5 text-paper font-medium text-[15px] leading-snug">{s.body}</p>
    <p className="mt-1.5 font-mono text-[12.5px] text-paper/45 leading-snug">{s.meta}</p>
  </div>
);

/* The base of the U: one wide bar, because everything turns around the store. */
const StoreBar = ({ s }) => (
  <div
    className={`rounded-lg p-5 ring-1 ${cardTone(s.accent)} flex flex-wrap items-center gap-x-6 gap-y-2`}
  >
    <StepHead s={s} />
    <p className="text-paper font-medium text-[15px] leading-snug">{s.body}</p>
    <p className="font-mono text-[12.5px] text-paper/45 leading-snug">{s.meta}</p>
  </div>
);

/**
 * How far each flow arrow spans between two cards.
 *
 * This is a layout constant, not decoration. Three of these stack up in each
 * arm, so it sets how tall the U stands, and because the panels inside the U
 * are centred against that height at a size of their own, it is also what
 * decides how much clear space sits above and below them. Shortening it
 * crowds those panels against the top card and the base bar.
 */
const FLOW_ARROW_LENGTH = 100;

/**
 * An arrow carrying what travels along it. `up` flips it for the return arm.
 *
 * `side` places the label on the *outside* of the U (left of the arrow on the
 * left arm, right of it on the right arm) so no label sits in the space the
 * two panels occupy. A matching flex spacer opposite the label keeps the arrow
 * centred on the column, and so on the cards above and below it.
 * `side="stack"` is the plain left-to-right form used by the linear fallback.
 */
const Edge = ({ label, up = false, accent = false, side = 'stack', className = '' }) => {
  const Icon = up ? ArrowUp : ArrowDown;
  const icon =
    side === 'stack' ? (
      <Icon
        className={`w-8 h-8 shrink-0 ${accent ? 'text-amber/80' : 'text-lime/70'}`}
        aria-hidden="true"
      />
    ) : (
      <FlowArrow
        dir={up ? 'up' : 'down'}
        length={FLOW_ARROW_LENGTH}
        className={accent ? 'text-amber/80' : 'text-lime/70'}
      />
    );
  const text = (
    <p
      className={`flex-1 min-w-0 font-mono text-[12.5px] text-paper/55 leading-snug ${
        side === 'left' ? 'text-right' : 'text-left'
      }`}
    >
      {label}
    </p>
  );

  if (side === 'stack') {
    return (
      <div className={`flex items-center gap-3 py-3.5 pl-3 ${className}`}>
        {icon}
        {text}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-4 py-4 ${className}`}>
      {side === 'left' ? text : <span className="flex-1" />}
      {icon}
      {side === 'left' ? <span className="flex-1" /> : text}
    </div>
  );
};

/* The two things that are not on the line. They sit inside the U because both
   attach to the store at its base: one reads from it, one writes back to it. */
const BranchBox = () => (
  <div className="rounded-lg border border-dashed border-lime/35 bg-lime/[0.04] p-6">
    <p className="label text-lime/70">{flowNotes.branchLabel}</p>
    <h3 className="mt-3 font-display font-semibold text-[1.25rem] text-paper">
      {flowNotes.branchTitle}
    </h3>
    <p className="mt-2.5 text-paper/65 text-[14px] leading-relaxed">{flowNotes.branchBody}</p>
  </div>
);

const ReturnBox = () => (
  <div className="rounded-lg bg-white/[0.03] ring-1 ring-white/10 p-6">
    <p className="label text-paper/50 flex items-center gap-2">
      <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
      {flowNotes.returnLabel}
    </p>
    <h3 className="mt-3 font-display font-semibold text-[1.25rem] text-paper">
      {flowNotes.returnTitle}
    </h3>
    <p className="mt-2.5 text-paper/65 text-[14px] leading-relaxed">{flowNotes.returnBody}</p>
  </div>
);

const System = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="Farm Tech · The system"
      title={systemMeta.name}
      lead={systemMeta.tagline}
    >
      <p className="label text-paper/40">{systemMeta.credit}</p>
    </PageBanner>

    {/* The one rule the whole design turns on */}
    <Section tone="soil" tight>
      <div className="max-w-4xl">
        <Eyebrow tone="dark">The design rule</Eyebrow>
        <p className="mt-5 font-display font-semibold text-[1.9rem] md:text-[2.6rem] leading-[1.15] text-paper">
          Learned models advise.{' '}
          <span className="text-amber">Only deterministic logic actuates.</span>
        </p>
        <Lead tone="dark" className="mt-5">
          Six layers carry a reading from the soil to a valve. A model can recommend and a language
          model can explain, but the only things permitted to open a valve are a deterministic decision
          path and a set of safety gates.
        </Lead>
      </div>
    </Section>

    {/* Six layers at a glance */}
    <Section tone="paper">
      <Head
        eyebrow="System overview"
        title="Six layers, one responsibility each"
        lead="Each hands a validated result to the next. The numbering is the order data actually travels."
      />
      <div className="mt-12 space-y-3">
        {layerGlance.map((l) => (
          <div
            key={l.n}
            className={`grid grid-cols-1 md:grid-cols-[auto_15rem_1fr] gap-x-6 gap-y-2 items-start rounded-xl p-5 ring-1 ${
              l.accent ? 'bg-amber/[0.07] ring-amber/30' : 'bg-chalk ring-soil/10'
            }`}
          >
            <Index n={l.n} accent={l.accent} />
            <div>
              <h3 className="font-display font-semibold text-[1.2rem] text-ink leading-tight">{l.name}</h3>
              <p className="label mt-1 text-soil/40">{l.sub}</p>
            </div>
            <p className="text-soil/70 text-[15px] leading-relaxed md:pt-1">{l.detail}</p>
          </div>
        ))}
      </div>
    </Section>

    {/* End-to-end data flow */}
    <Section tone="ink" className="fieldgrid">
      <Head
        tone="dark"
        eyebrow="The whole system, one picture"
        title="End-to-end data flow"
        lead="Telemetry runs down the left arm, turns at the store, and comes back up the right arm as a command. The two panels inside the U are the only parts that sit off that line."
      />

      {/* ---- Desktop: the U. Down the left arm, across the base, up the right. ---- */}
      <div className="hidden xl:block mt-12" aria-hidden="true">
        <p className="label text-lime mb-6">{flowNotes.chainLabel}</p>

        {/* Narrow middle, wide arms: the panels get a column that makes them
            tall enough to fill the U, and each arm gets room to carry its
            arrow in the centre with the label outboard of it. The diagram
            takes the full container width; Tailwind's container already caps
            it at 2xl, and the arms need every pixel to keep part numbers and
            edge labels on one line. */}
        <div>
          {/* Each rank of the U is one shared grid row, so the left and right
              cards of a rank sit in the same row and cannot drift apart.

              They used to be two independent flex columns spread with
              justify-between. That only lined up while both arms happened to
              be the same height: the moment one side's copy wrapped to an
              extra line, the shorter arm redistributed its slack and every
              card below the wrap slid out of alignment with its opposite
              number. Ranks make the alignment structural instead: a row is
              as tall as the taller of its two cards, and both stretch to it. */}
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,20rem)_minmax(0,1fr)] gap-x-12 xl:gap-x-16">
            {/* Left arm: 01 → 04, flowing down */}
            <FlowCard s={step(1)} className="col-start-1 row-start-1" />
            <Edge label={step(1).edge} side="left" className="col-start-1 row-start-2" />
            <FlowCard s={step(2)} className="col-start-1 row-start-3" />
            <Edge label={step(2).edge} side="left" className="col-start-1 row-start-4" />
            <FlowCard s={step(3)} className="col-start-1 row-start-5" />
            <Edge label={step(3).edge} side="left" className="col-start-1 row-start-6" />

            {/* Inside the U: the two panels that are not on the line. They
                span every rank and centre against the diagram as a whole. */}
            <div className="col-start-2 row-start-1 row-span-6 flex flex-col justify-center gap-8">
              <BranchBox />
              <ReturnBox />
            </div>

            {/* Right arm: 05 → 07, flowing up, so it reads bottom to top */}
            <FlowCard s={step(7)} className="col-start-3 row-start-1" />
            <Edge label={step(6).edge} up accent side="right" className="col-start-3 row-start-2" />
            <FlowCard s={step(6)} className="col-start-3 row-start-3" />
            <Edge label={step(5).edge} up accent side="right" className="col-start-3 row-start-4" />
            <FlowCard s={step(5)} className="col-start-3 row-start-5" />
            <Edge label={step(4).edge} up side="right" className="col-start-3 row-start-6" />
          </div>

          {/* Base of the U */}
          <StoreBar s={step(4)} />
        </div>
      </div>

      {/* ---- Narrow screens, and the reading order for assistive tech ---- */}
      <div className="mt-12 xl:sr-only">
        <p className="label text-lime mb-5">{flowNotes.chainLabel}</p>
        <ol>
          {dataFlow.map((s) => (
            <li key={s.id}>
              <FlowCard s={s} />
              {s.edge && <Edge label={s.edge} accent={s.accent} />}
            </li>
          ))}
          <li className="flex items-start gap-3 pt-4 pl-4">
            <RotateCcw className="w-5 h-5 text-paper/40 shrink-0" aria-hidden="true" />
            <p className="font-mono text-[12.5px] text-paper/50 leading-snug pt-0.5">
              status uplink → back to step 04
            </p>
          </li>
        </ol>

        {/* Same two panels as the U carries in its middle column, so they take
            the same 32px between them here. The larger margin above keeps the
            pair reading as a pair rather than as two more entries after the
            chain above it. */}
        <div className="mt-12 space-y-8">
          <BranchBox />
          <ReturnBox />
        </div>
      </div>
    </Section>

    {/* Guiding invariants */}
    <Section tone="paper">
      <Head
        eyebrow="Guiding invariants"
        title="The principles every choice answers to"
        lead="Every design decision answers to these. They were settled first, and nothing was adopted that could not honour them."
      />
      {/* Five cards. In a plain two- or three-column grid the last row comes
          up short and the hole sits at the right edge like a missing card.
          Doubling the tracks and spanning each card across two lets the short
          row be centred by starting its cards one track in. The start
          columns below are sized for exactly five; revisit if the count
          changes. */}
      <div className="mt-12 grid md:grid-cols-4 lg:grid-cols-6 gap-5">
        {invariants.map((inv, i) => (
          <Card
            key={inv.n}
            className={`p-6 md:col-span-2 ${i === 3 ? 'lg:col-start-2' : ''} ${
              i === 4 ? 'md:col-start-2 lg:col-start-4' : ''
            }`}
          >
            <Index n={inv.n} />
            <h3 className="mt-4 font-display font-semibold text-[1.15rem] text-ink">{inv.title}</h3>
            <p className="mt-2.5 text-soil/70 text-[14.5px] leading-relaxed">{inv.body}</p>
          </Card>
        ))}
      </div>
    </Section>

    {/* Control panel modules */}
    <Section tone="soil">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <Head
            tone="dark"
            eyebrow="Module breakdown · control panel"
            title="What runs on the edge server"
            lead="Six services share one box. Everything safety-critical among them runs with no dependency on the internet."
          />
        </div>
        <SideImage
          tone="dark"
          src={edgeMonitoring}
          alt="An engineer working in the greenhouse beside live sensor readouts"
        />
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {controlPanelModules.map((m) => (
          <Card key={m.title} tone="dark" className="p-6">
            <h3 className="font-display font-semibold text-[1.1rem] text-paper">{m.title}</h3>
            <p className="mt-2.5 text-paper/60 text-[14.5px] leading-relaxed">{m.body}</p>
          </Card>
        ))}
      </div>
    </Section>

    {/* The selection, in one rule */}
    <Section tone="ink">
      <Head
        tone="dark"
        eyebrow="The design, in one rule"
        title={systemMeta.rule}
        center
      />
      <div className="mt-12 grid md:grid-cols-2 gap-4 max-w-5xl mx-auto">
        {closingRules.map((r, i) => (
          <div
            key={r}
            className="flex items-start gap-4 rounded-lg bg-white/[0.04] ring-1 ring-white/10 p-5"
          >
            <Index n={i + 1} tone="dark" />
            <p className="text-paper/75 text-[14.5px] leading-relaxed pt-1">{r}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center">
        <Link
          to="/farm-tech/architecture"
          className="inline-flex items-center gap-2.5 rounded-full bg-lime text-ink px-7 py-3.5 font-semibold hover:bg-paper transition-colors"
        >
          Read the architecture layer by layer
          <ArrowRight className="w-[18px] h-[18px]" />
        </Link>
      </div>
    </Section>
  </div>
);

export default System;
